import { useMemo, useState } from "react"
import ReactDOM from "react-dom"
import { useDispatch, useSelector } from "react-redux"

import { createSosCharge } from "../../../../lib/fetchApi"
import { cptOptions, outsideClinicOptions, returnCptBaseOnId } from "../../../../lib/systemData"
import { useUserTabEditPermissionDisabled } from "../../../../lib/usersPermissions"
import Button from "../../../common/Button"
import Modal from "../../../common/Modal"
import ModalInnerWrapper from "../../../common/ModalInnerWrapper"
import Backdrop from "../../../common/modal/Backdrop"
import CustomSelect from "../../../common/interactive/CustomSelect"
import DatepickerWithLabel from "../../../common/interactive/DatepickerWithLabel"
import InputBox from "../../../common/interactive/InputBox"
import PannelBody from "../../../fragments/PannelBody"

function formatErrors(errors) {
    if (!errors) {
        return null
    }
    if (typeof errors === "string") {
        return errors
    }
    if (Array.isArray(errors)) {
        return errors.filter(Boolean).join(" ")
    }
    if (typeof errors === "object") {
        if (errors.errors) {
            return formatErrors(errors.errors)
        }
        return Object.values(errors).flat().filter(Boolean).join(" ")
    }

    return "Unable to add SOS charge."
}

const AddSosModal = ({ closeModal }) => {
    const dispatch = useDispatch()
    const activeCase = useSelector(state => state.case.activeCase)
    const userToken = useSelector(state => state.user.token)
    const sosEditDisabled = useUserTabEditPermissionDisabled('case_sos')

    const locations = useMemo(() => {
        return (outsideClinicOptions() || []).filter(item => item.sos_billing == 1 || item.sos_billing === true)
    }, [])
    const cpts = useMemo(() => cptOptions() || [], [])

    const [dateOfService, setDateOfService] = useState("")
    const [outsideLocationId, setOutsideLocationId] = useState(null)
    const [cptId, setCptId] = useState(null)
    const [accountNo, setAccountNo] = useState("")
    const [area, setArea] = useState("")
    const [isLoading, setIsLoading] = useState(false)
    const [errorMessage, setErrorMessage] = useState(null)

    const canSubmit = dateOfService && outsideLocationId && cptId

    const updateSelect = setter => data => {
        setter(data?.data?.value ?? null)
    }
    const updateValue = setter => data => {
        setter(data?.data?.value ?? "")
    }
    const updateCpt = data => {
        const nextCptId = data?.data?.value ?? null
        setCptId(nextCptId)
        const cpt = returnCptBaseOnId(nextCptId)
        setArea(cpt?.area || "")
    }

    const closeModalHandler = () => {
        if (!isLoading) {
            closeModal()
        }
    }

    const submitHandler = async event => {
        event.preventDefault()
        if (sosEditDisabled || !activeCase?.case?.id || !canSubmit) {
            return
        }

        setIsLoading(true)
        setErrorMessage(null)

        const response = await createSosCharge(userToken, activeCase.case.id, {
            date_of_service: dateOfService,
            outside_location_id: outsideLocationId,
            cpt_id: cptId,
            account_no: accountNo || null,
            area: area || null,
        }, dispatch)

        setIsLoading(false)

        if (response?.errorMessage) {
            setErrorMessage(formatErrors(response.errorMessage))
            return
        }

        closeModal()
    }

    return <>
        {ReactDOM.createPortal(
            <Backdrop hide={closeModalHandler} />,
            document.getElementById("backdrop-root")
        )}
        {ReactDOM.createPortal(
            <Modal>
                <ModalInnerWrapper closeModal={closeModalHandler} modalTitle={"Add SOS"} setWidth={460}>
                    <PannelBody>
                        <form onSubmit={submitHandler}>
                            <p>This adds a line to the SOS list. The charge is added to billing when Completed is checked.</p>
                            <div className="row">
                                <DatepickerWithLabel
                                    label="Date of Service"
                                    defaultDate={dateOfService}
                                    action={updateValue(setDateOfService)}
                                    required={true}
                                />
                            </div>
                            <div className="row">
                                <CustomSelect
                                    label="Outside Location"
                                    optionsData={locations}
                                    preSelectedValue={outsideLocationId}
                                    action={updateSelect(setOutsideLocationId)}
                                    required={true}
                                />
                            </div>
                            <div className="row">
                                <CustomSelect
                                    label="CPT"
                                    optionsData={cpts}
                                    preSelectedValue={cptId}
                                    action={updateCpt}
                                    required={true}
                                    complexLabel={true}
                                />
                            </div>
                            <div className="row">
                                <InputBox
                                    label="Account #"
                                    currentValue={accountNo}
                                    action={updateValue(setAccountNo)}
                                />
                            </div>
                            <div className="row">
                                <InputBox
                                    label="Area"
                                    currentValue={area}
                                    action={updateValue(setArea)}
                                />
                            </div>
                            {errorMessage && <div className="row"><span className="danger">{errorMessage}</span></div>}
                            <div className="row flex-container">
                                <Button type="button" btnStyle="btn_light_grey" click={closeModalHandler}>Cancel</Button>
                                <Button type="submit" isLoading={isLoading} isDisabled={sosEditDisabled || !canSubmit}>Save</Button>
                            </div>
                        </form>
                    </PannelBody>
                </ModalInnerWrapper>
            </Modal>,
            document.getElementById("overlay-root")
        )}
    </>
}

export default AddSosModal
